import { percent } from "@justhome/common";
import {
  Application,
  Assumptions,
  Costs,
  Preferences,
  Property,
} from "@justhome/api-core";
import { ApplicationTypeEnum } from "@lib/api/enums";
import { ApplicationFormValues } from "./types";

export const getSpecialApplicationData = (
  data: ApplicationFormValues,
): Partial<
  Pick<
    Application,
    "equity" | "monthlyNetIncome" | "loanAmount" | "refinancingStartDate"
  >
> => {
  switch (data.applicationType) {
    case ApplicationTypeEnum.BUY_WITH_SAVINGS:
      return {
        equity: data.downPayment,
        monthlyNetIncome: data.monthlyNetIncome,
      };
    case ApplicationTypeEnum.BUILD_HOUSE:
      return {
        equity: data.downPayment,
        monthlyNetIncome: data.monthlyNetIncome,
      };
    case ApplicationTypeEnum.REFINANCING:
      return {
        loanAmount: data.loanAmount,
        refinancingStartDate: data.loanDate,
        monthlyNetIncome: data.monthlyNetIncome,
      };
    default:
      return {};
  }
};

export const getPreferences = (): Preferences => {
  return { repaymentRate: 1 };
};

export const getCosts = (
  data: ApplicationFormValues,
  assumptions: Assumptions,
) => {
  const {
    realEstateAgentFeePercentage,
    notaryFeePercentage,
    propertyTransferTaxPercentage,
    notaryFeeReducedPercentage,
  } = assumptions;

  let costs: Costs;
  let realEstateAgentFee: number;

  switch (data.applicationType) {
    case ApplicationTypeEnum.BUILD_HOUSE:
      realEstateAgentFee = data.hasRealEstateAgentCommission
        ? data.realEstateAgentCommission!
        : realEstateAgentFeePercentage;

      costs = {
        landOwned: data.ownsLandplot,
        landPurchasePrice: data.purchasePrice,
        propertyTransferTax: data.ownsLandplot
          ? 0
          : percent(data.purchasePrice!, propertyTransferTaxPercentage),
        notaryFee: data.ownsLandplot
          ? percent(data.constructionRelatedCosts, notaryFeeReducedPercentage)
          : percent(data.purchasePrice!, notaryFeePercentage),
        construction: data.constructionRelatedCosts,
        realEstateAgentFee: data.ownsLandplot
          ? 0
          : percent(data.purchasePrice!, realEstateAgentFee),
      };
      break;
    case ApplicationTypeEnum.BUY_WITH_SAVINGS:
      realEstateAgentFee = data.hasRealEstateAgentCommission
        ? data.realEstateAgentCommission!
        : realEstateAgentFeePercentage;
      costs = {
        purchasePrice: data.purchasePrice,
        notaryFee: percent(data.purchasePrice, notaryFeePercentage),
        propertyTransferTax: percent(
          data.purchasePrice,
          propertyTransferTaxPercentage,
        ),
        realEstateAgentFee: percent(data.purchasePrice, realEstateAgentFee),
      };
      break;
    default: {
      costs = {};
      break;
    }
  }
  return costs;
};

const sumCosts = (costs: Costs): number => {
  return Object.values(costs).reduce((sum: number, value) => {
    // Make sure that the value is a number
    if (value && typeof value === "number") {
      return sum + value;
    } else {
      return sum;
    }
  }, 0);
};

export const getProperty = (data: ApplicationFormValues): Property => {
  let property: Property;

  switch (data.applicationType) {
    case ApplicationTypeEnum.BUILD_HOUSE:
      property = {
        address: {
          postalCode: data.postalCode,
        },
        type: data.typeOfProperty,
      };
      break;
    case ApplicationTypeEnum.REFINANCING:
      property = {
        address: {
          postalCode: data.postalCode,
        },
        marketValue: data.marketValue,
      };

      break;
    case ApplicationTypeEnum.BUY_WITH_SAVINGS:
    default:
      property = {
        type: data.typeOfProperty,
        address: {
          postalCode: data.postalCode,
        },
      };
      break;
  }

  return property;
};

const positiveNumber = (num?: string | number) => {
  return Math.max(Number(num), 0);
};

export const getLoanAmount = (data: ApplicationFormValues, costs: Costs) => {
  let loanAmount;
  switch (data.applicationType) {
    case ApplicationTypeEnum.REFINANCING:
      loanAmount = data.loanAmount;
      break;
    case ApplicationTypeEnum.BUILD_HOUSE:
    case ApplicationTypeEnum.BUY_WITH_SAVINGS:
      loanAmount = sumCosts(costs) - data.downPayment;
      break;
    default:
      loanAmount = 0;
  }
  return positiveNumber(loanAmount);
};
